import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Divider, Menu, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { VESSEL_TYPE } from "../../constants/constants";
import { SEP_CHART_LIST } from "../../constants/trendGraph/sepLines";
import { ENGINE_CHART_LIST } from "../trendPage/engine/engineList";
import { GRAPH_TYPE } from "../../constants/trendGraph/trendGraphType";
import { useSelector } from "react-redux";
import { selectedChartType } from "../../model/slice/chartTypeSlice";
import localizationKeys from "../../i18n/localizationKeys";
import { chartsData } from "../../model/slice/chartsSlice";
import { DEFAULT_CUSTOM_CHART_KEY } from "../../constants/trendGraph/charts";
import PropTypes from "prop-types";
import { ELECTRIC_CHART_LIST } from "../../constants/trendGraph/electricLines";
import { ADDITIONAL_ELECTRIC_TREND_LIST } from "../../constants/trendGraph/additionalElectricTrend";

const useStyles = ({ isMainPage }) => ({
    menu: {
      "& .MuiMenu-paper": {
        backgroundColor: isMainPage ? "rgba(2, 2, 2, 0.7) !important" : "#46576d",
        marginTop: "2px",
        marginLeft: "13px",
        color: "#fff",
        padding: "8px 0",
      },
      "& .MuiListItem-root.Mui-selected, & .MuiListItem-root.Mui-selected:hover": {
        backgroundColor: "#252d39 !important",
      },
    },
    menuItem: {
      padding: "8px 24px",
      fontSize: "14px",
      width: "240px",
      "&:hover": {
        backgroundColor: "#252d39",
      },
    },
    item: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    divider: {
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      margin: "12px 24px",
    },
  });

export const IconMenu = (props) => {
  const { vessel, anchorEl, setAnchorEl } = props;
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const styles = useStyles({
    isMainPage: location.pathname.includes("/main"),
  });
  const open = useMemo(() => !!anchorEl, [anchorEl]);
  const [chartList, setChartList] = useState([]);
  const navigate = useNavigate();
  const selectedChart = useSelector(selectedChartType);
  const customCharts = useSelector(chartsData);
  const isSepVessel = vessel?.vesselType === VESSEL_TYPE.SEP;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (graphId, chartId) => {
    if (graphId) {
      navigate(`/trend/${graphId}/${chartId}/${vessel.id}`);
    }
    handleClose();
  };

  const showAdd = useCallback(
    () => !customCharts?.charts || customCharts.charts.length < 16,
    [customCharts]
  );

  useEffect(() => {
    setChartList([]);
  }, []);

  useEffect(async () => {
    if (!vessel) return;
    let charts = [];
    if (isSepVessel) {
      charts = SEP_CHART_LIST.map((chart) => ({
        id: chart.id,
        graphId: GRAPH_TYPE.SEP,
        name: t(chart.title),
      }));
    } else {
      if (vessel.NoME > 0) {
        charts = ENGINE_CHART_LIST.map((chart) => ({
          id: chart.id,
          graphId: GRAPH_TYPE.ENGINE,
          name: t(chart.title),
        }));
      }
    }

    if (vessel.NoDG > 0 || vessel.NoSG > 0) {
      const chartList = isSepVessel ? ADDITIONAL_ELECTRIC_TREND_LIST : ELECTRIC_CHART_LIST
      charts.push(
        ...chartList.map((chart) => ({
          id: chart.id,
          graphId: GRAPH_TYPE.ELECTRIC,
          name: t(chart.title),
        }))
      );
    }

    if (customCharts?.charts) {
      charts.push(
        ...customCharts.charts.map((chart) => ({
          id: chart.id,
          graphId: GRAPH_TYPE.CUSTOM,
          name: chart.name,
        }))
      );
    }

    setChartList(Array.from(new Map(charts.map((chart) => [chart.id, chart])).values()));
  }, [vessel, customCharts, i18n.language]);

  return (
    <Menu
      id="basic-menu"
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onMouseLeave={handleClose}
      sx={styles.menu}
      slotProps={{
        list: {"aria-labelledby": "basic-button"},
        paper: {
          elevation: 0,
          sx: {filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))"}
        }
      }}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "left",
      }}
    >
      <div onMouseLeave={handleClose}>
        {" "}
        {/* Wraped the menu items in a div */}
        {chartList?.map((chart) => (
          <MenuItem
            sx={styles.menuItem}
            onClick={() => handleClick(chart.graphId, chart.id)}
            key={chart.id}
            selected={chart.id === selectedChart}
          >
            <Box component={"div"} sx={styles.item}>{chart.name}</Box>
          </MenuItem>
        ))}
        {showAdd() && chartList?.length > 0 && <Divider sx={styles.divider} />}
        {showAdd() && (
          <MenuItem
            sx={styles.menuItem}
            onClick={() => handleClick(GRAPH_TYPE.CUSTOM, DEFAULT_CUSTOM_CHART_KEY)}
            key={DEFAULT_CUSTOM_CHART_KEY}
            selected={false}
          >
            + {t(localizationKeys.CustomMenuAdd)}
          </MenuItem>
        )}
      </div>
    </Menu>
  );
};

IconMenu.propTypes = {
  vessel: PropTypes.object || null,
  anchorEl: PropTypes.object || null,
  setAnchorEl: PropTypes.func,
}